import {ref, computed, watch, type Ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {CategoryManager} from '@/library/category';
import store from '@/store';
import {errorHandler} from '@/utils/portuna';
import dateHelper from '@/utils/portuna/dateHelper';
import {getDateInformation} from '@/library/date';
import LocalStorage from '@/library/localStorage';
import {strtotime} from '@/library/strtotime';
import {NoOperation} from '@/library/utils';
import {calendarResourceOptions} from '@/library/modules/calendar/options';

let currentCalendar: Nullable<CalendarData>;
let optionFetchPromise: Nullable<Promise<ServiceCalendarResourceOptions>> = null;

export function useCalendar(
    resource: Ref<Optional<CalendarResources>>,
    overwrites: Optional<Partial<{
        hasTileView: Nullable<boolean>,
        routes: Optional<Record<'list' | 'view' | 'add' | 'edit' | 'copy' | 'request', string>>
        options: Optional<CalendarResourceOptions>
    }>> = null,
)
{
    const categoryManager        = new CategoryManager();
    const serviceResourceOptions = ref<ServiceCalendarResourceOptions>();
    const zoom                   = ref(1);

    const route  = useRoute();
    const router = useRouter();

    const hourHeight      = computed(() => store.state.calendarHourHeight);
    const minuteHeight    = computed(() => store.getters.calendarMinuteHeight);
    const halfHourHeight  = computed(() => hourHeight.value / 2);
    const dailyHourHeight = computed(() => hourHeight.value * 24);

    const calendarHasTileView = computed<boolean>(() => overwrites && overwrites.hasTileView ? overwrites.hasTileView : false);

    const routes = computed(() =>
    {
        if (overwrites && overwrites.routes)
        {
            return overwrites.routes;
        }

        return {
            list: resource.value + '-list',
            view: resource.value + '-entry-view',
            add: resource.value + '-entry-add',
            edit: resource.value + '-entry-edit',
            copy: resource.value + '-entry-copy',
            request: resource.value + '-entry-request',
        };
    });

    const options = computed<CalendarResourceOptions | ServiceCalendarResourceOptions>(() =>
    {
        if (overwrites && overwrites.options)
        {
            return overwrites.options as CalendarResourceOptions;
        }

        if (resource.value === 'services')
        {
            return (serviceResourceOptions.value || {}) as ServiceCalendarResourceOptions;
        }

        return calendarResourceOptions[resource.value!] as CalendarResourceOptions;
    });

    const calendarViewType = computed(() =>
    {
        const defaultView = calendarHasTileView.value ? 'tile' : 'week';
        const type        = route.params.type || LocalStorage.get(resource.value + '.calendar.lastView', defaultView);
        const views       = ['overview', 'day', 'week', '5days', '7days', 'month', 'table', 'request'];

        if (calendarHasTileView.value)
        {
            views.push('tile');
        }

        return views.includes(type) ? type : defaultView;
    });

    const groupViewType = computed(() =>
    {
        const type         = route.params.type || LocalStorage.get(resource.value! + '.group.lastView', 'tile');
        let availableViews = ['tile', 'day', 'week', '5days', '7days', 'month', 'table', 'request'];

        if (resource.value! === 'services')
        {
            availableViews = ['day', 'week', 'tile'];
        }

        return availableViews.includes(type) ? type : 'tile';
    });

    const calendarViewDate = computed(() => route.params.date || LocalStorage.get(resource.value! + '.lastDate', dateHelper('Y-m-d')));

    const todayLinkParams = computed(() => ({
        name: route.name,
        params: {
            id: route.params.id,
            type: route.params.type || getViewType(currentCalendar),
            date: 'today',
        },
    }));

    const today = computed<string>(() => dateHelper('Y-m-d') as string);

    const isToday = computed<boolean>(() =>
    {
        const currentDateInformation = getDateInformation(calendarViewDate.value);
        const todayDateInformation   = getDateInformation(today.value!);
        const viewType               = getViewType(currentCalendar);

        return todayDateInformation.date === currentDateInformation.date ||
            (viewType === 'week' && todayDateInformation.year === currentDateInformation.year && todayDateInformation.week === currentDateInformation.week) ||
            (viewType === 'month' && todayDateInformation.year === currentDateInformation.year && todayDateInformation.month === currentDateInformation.month);
    });

    const monthDays = computed(() =>
    {
        const start = strtotime('monday this week', dateHelper('U', dateHelper('Y-m-01', calendarViewDate.value)));
        const end   = strtotime('this week sunday', dateHelper('U', dateHelper('Y-m-t', calendarViewDate.value)));
        let current = start;
        const days  = [];
        let row     = [];
        let index   = 0;

        do
        {
            index++;
            row.push(getDateInformation(current));
            current = strtotime('+1 day', current);
            if (index % 7 === 0)
            {
                days.push(row);
                row   = [];
                index = 0;
            }
        }
        while (current <= end);

        return days;
    });

    function changeCalendarEntry(calendar: CalendarData)
    {
        currentCalendar = calendar;
    }

    function handleResourceOptions()
    {
        if (resource.value! === 'services')
        {
            if (!optionFetchPromise)
            {
                optionFetchPromise = store.dispatch('module/services/fetchServiceOptions');
            }

            optionFetchPromise.then(serviceResourceOptionsData =>
            {
                serviceResourceOptions.value = {...calendarResourceOptions['services'], ...serviceResourceOptionsData};
            });

            return optionFetchPromise;
        }

        return Promise.resolve();
    }

    function getViewType(entry: Nullable<CalendarData>): ViewTypeOptionKey
    {
        let view = 'week';

        if (entry)
        {
            if (entry.group)
            {
                view = groupViewType.value;
            }
            else
            {
                view = calendarViewType.value;

                if (resource.value! === 'services')
                {
                    if (!['day', 'week'].includes(view))
                    {
                        view = 'week';
                    }
                }
                else if (entry.all_day_only && ['day', 'week', '5days', '7days'].includes(view))
                {
                    view = 'month';
                }
            }
        }

        return (view === 'list' ? 'table' : view) as ViewTypeOptionKey;
    }

    function getType(entry: CalendarData | CurrentCalendarData)
    {
        return entry.group ? 'group' : (entry.link ? 'link' : 'calendar');
    }

    function getTypeName(entry: CalendarData)
    {
        return {
            calendar: options.value!.calendarType,
            group: 'Ordner',
            link: 'Verbund',
        }[getType(entry)];
    }

    function getTypeIcon(entry: CalendarData)
    {
        return {
            calendar: options.value!.calendarIcon,
            group: 'folder',
            link: 'link',
        }[getType(entry)];
    }

    function getTypeIconColor(entry: CalendarData)
    {
        const icons = {
            calendar: options.value!.moduleColor,
            group: '#cd7f0f',
            link: null,
        };

        return entry.activated ? icons[getType(entry)] : null;
    }

    function goToToday()
    {
        router.push(todayLinkParams.value).catch(errorHandler);
    }

    function goToDate(date: string)
    {
        if (calendarViewDate.value !== date)
        {
            router.push({
                name: route.name,
                params: {
                    id: route.params.id,
                    type: route.params.type || getViewType(currentCalendar),
                    date,
                },
            }).catch(errorHandler);
        }
    }

    function goToCalendar(calendar: number | string)
    {
        calendar = String(calendar);
        if (calendar === String(route.params.id))
        {
            return;
        }
        router.push({
            name: route.name,
            params: {
                id: calendar,
                type: route.params.type,
                date: route.params.date || calendarViewDate.value,
            },
        }).catch(errorHandler);
    }

    function applySerialChanges(entries: Nullable<CalendarEntryListData[]>, entry: CalendarEntryData | CalendarEntryListData, serial: boolean = false, callback: ({
        entries,
        entry,
        index,
    }: { entries: Nullable<CalendarEntryListData[]>, entry: CalendarEntryListData, index: number }) => void)
    {
        if (entries)
        {
            for (let i = entries.length - 1; i >= 0; i--)
            {
                if (
                    entries[i].id === entry.id &&
                    (entries[i].recurrence_start === entry.recurrence_start ||
                        (serial && entries[i].recurrence_start > entry.recurrence_start))
                )
                {
                    callback({
                        entries,
                        entry: entries[i],
                        index: i,
                    });
                }
            }
        }
    }

    watch(zoom, (newValue) =>
    {
        store.commit('setCalendarHourHeight', Number(newValue) * 60);
    });

    handleResourceOptions().then(NoOperation);

    zoom.value = store.getters.calendarMinuteHeight;

    return {
        categoryManager,
        calendarHasTileView,
        serviceResourceOptions,
        zoom,
        hourHeight,
        minuteHeight,
        halfHourHeight,
        dailyHourHeight,
        routes,
        options,
        calendarViewType,
        groupViewType,
        calendarViewDate,
        todayLinkParams,
        today,
        isToday,
        monthDays,
        changeCalendarEntry,
        handleResourceOptions,
        getViewType,
        getType,
        getTypeName,
        getTypeIcon,
        getTypeIconColor,
        goToToday,
        goToDate,
        goToCalendar,
        applySerialChanges,
    };
}
